import { useState } from "react";
import { clsx } from "clsx";
import { ApiKey } from "../../redux/reducers/userSlice";
import { Button, Text, View, Card } from "../base";
import { Icon } from "../base/Icon";
import { API_URL } from "../../config";
import { ConfirmDialog } from "../ConfirmDialog";

type ApiKeyListProps = {
  apiKeys: ApiKey[];
  onCreateKey: () => void;
  onDeleteKey: (keyId: string) => void;
  className?: string;
  isLoading?: boolean;
};

export const ApiKeyList = ({ apiKeys, onCreateKey, onDeleteKey, className, isLoading }: ApiKeyListProps) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingKey, setDeletingKey] = useState<ApiKey | null>(null);

  const handleDeleteClick = (key: ApiKey) => {
    setDeletingKey(key);
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (deletingKey) {
      onDeleteKey(deletingKey.id);
      setShowDeleteDialog(false);
      setDeletingKey(null);
    }
  };

  return (
    <Card className={`p-6 ${className || ""}`}>
      <View className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4">
        <Text className="text-lg font-semibold mb-3 sm:mb-0">API Keys</Text>
        <Button
          onClick={onCreateKey}
          variant="brand"
          className="w-full sm:w-auto"
        >
          Create New Key
        </Button>
      </View>
      {isLoading ? (
        <Text
          variant="muted"
          className="text-center py-8"
        >
          Loading...
        </Text>
      ) : !apiKeys.length ? (
        <Text
          variant="muted"
          className="text-center py-8"
        >
          No API keys yet. Create one to get started.
        </Text>
      ) : (
        <View className="grid grid-cols-1 gap-4">
          {apiKeys.map((key) => (
            <View
              key={key.id}
              className={clsx(
                "p-4 rounded-lg",
                "bg-gray-50 dark:bg-gray-700/50",
                "border border-gray-200 dark:border-gray-600",
                "flex justify-between items-center",
              )}
            >
              <View className="flex flex-col">
                <Text
                  variant="body"
                  className="font-semibold"
                >
                  {key.description || "Unnamed Key"}
                </Text>
                <Text
                  variant="muted"
                  className="text-sm"
                >
                  {key.lastUsedAt && `Last used ${new Date(key.lastUsedAt).toLocaleDateString()} • `}
                  Created {new Date(key.createdAt).toLocaleDateString()}
                </Text>
              </View>
              <Button
                onClick={() => handleDeleteClick(key)}
                type="button"
                variant="ghost"
                className="!w-8 !h-8 !rounded-full"
              >
                <Icon
                  name="trash"
                  size={18}
                />
              </Button>
            </View>
          ))}
        </View>
      )}

      {/* <View className="flex flex-col mt-6">
        <Text variant="muted">
          Documentation for the API can be found{" "}
          <a
            href={`${API_URL}/docs`}
            target="_blank"
            className="text-brand hover:text-brand-light transition-colors"
          >
            here
          </a>
          .
        </Text>
      </View> */}

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setDeletingKey(null);
        }}
        onConfirm={confirmDelete}
        title="Delete API Key"
        message={`Are you sure you want to delete the API key "${deletingKey?.description || "Unnamed Key"}"?`}
      >
        <Text className="text-sm text-gray-600 dark:text-gray-400">
          This action cannot be undone. The API key will be permanently revoked and any applications using it will stop working.
        </Text>
      </ConfirmDialog>
    </Card>
  );
};
